import {
  type Dispatch,
  forwardRef,
  type ForwardRefRenderFunction,
  type SetStateAction,
  useMemo,
  type ReactNode,
} from "react";
import { styled } from "@mui/material/styles";
import { AcquireBookDataGrid } from "@/components/DataGrid";
import {
  type GridColDef,
  type GridRenderCellParams,
  type GridValueFormatterParams,
} from "@mui/x-data-grid";
import { useApiClient } from "@/hooks/useApiClient";
import {
  Box,
  Link,
  Stack,
  Typography,
  Button as MuiButton,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import {
  type GetPropertySummaryListAPIResponse,
  type PropertySummaryFeedGridProps,
  type PropertySummaryRow,
} from "@/features/propertySummary/types";
import { useGridPagination } from "../hooks/useGridPagination";
import { HttpStatusCode, isAxiosError } from "axios";
import { toast } from "react-toastify";
import PropertySummaryActionCell from "@/features/propertySummary/components/PropertySummaryActionCell";
import { GetPropertySummaryListAPI } from "@/features/propertySummary/api";
import { format } from "date-fns";
import Tooltip from "@mui/material/Tooltip";

const StyledDataGrid = styled(AcquireBookDataGrid)`
  // テーブルのセルをクリックしても青い枠が表示されないようにする
  & .MuiDataGrid-cell:focus-within,
  & .MuiDataGrid-cell:focus {
    outline: none !important;
  }

  // テーブルのカラムヘッダをクリックしても青い枠が表示されないようにする
  & .MuiDataGrid-columnHeader:focus-within,
  & .MuiDataGrid-columnHeader:focus {
    outline: none !important;
  }

  // テーブルヘッダーの背景色を設定
  .MuiDataGrid-columnHeaders {
    line-height: 1.5em !important;
  }

  // テーブルタイトルを太字に
  .MuiDataGrid-columnHeaderTitle {
    font-weight: 700;
  }
`;

interface Reason {
  reasonText: string;
  url: string;
}

// 失敗した場合にツールチップで原因と、原因データへのリンク表示する
const failTooltipMessage = (row: PropertySummaryRow): ReactNode => {
  const reasons: Reason[] = [];
  if (row.pictureRequest && row.pictureRequest.status === "SOME_FAILED") {
    const reason = {
      reasonText: "図面の取得に失敗しました",
      url: `/acquirebookstatus/${row.pictureRequest.id}`,
    };
    reasons.push(reason);
  }
  if (
    row.allMattersAnalyzeRequest &&
    row.allMattersAnalyzeRequest.status === "failed"
  ) {
    const reason = {
      reasonText: "全部事項解析に失敗しました",
      url: `/allmatters/detail/${row.allMattersAnalyzeRequest.id}`,
    };
    reasons.push(reason);
  }

  return (
    <Tooltip
      title={
        <Stack spacing={0.5}>
          {reasons.map((reason, index) => (
            <Box mb={1} key={index}>
              <Typography variant="body2" component={"span"}>
                {reason.reasonText}
              </Typography>
              <Link
                ml={1}
                color="info.light"
                sx={{ textDecoration: "underline" }}
                href={reason.url}
                target="_blank"
                rel="noopener noreferrer"
                variant="body2"
              >
                詳細を見る
              </Link>
            </Box>
          ))}
        </Stack>
      }
      arrow
      placement="top"
      enterDelay={200}
    >
      <MuiButton
        variant="text"
        color="error"
        disableRipple
        sx={{ cursor: "default" }}
      >
        失敗
      </MuiButton>
    </Tooltip>
  );
};

const makeGridColDef = (
  setEditModalOpen: Dispatch<SetStateAction<boolean>>,
  setSheetId: Dispatch<SetStateAction<number | null>>
): GridColDef[] => {
  return [
    {
      field: "id",
      headerName: "No",
      width: 80,
      sortable: false,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "prefectureName",
      headerName: "都道府県",
      width: 80,
      sortable: false,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "location",
      headerName: "所在",
      width: 260,
      sortable: false,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "address",
      headerName: "住居表示",
      width: 260,
      sortable: false,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "chiban",
      headerName: "地番",
      width: 100,
      sortable: false,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "kaokuNumbers",
      headerName: "家屋番号",
      width: 170,
      sortable: false,
      align: "left",
      headerAlign: "left",
      renderCell: ({ value }: GridRenderCellParams<string[]>) => {
        if (value && value.length > 0) {
          // 代表家屋番号
          const primaryKaokuNumber = value[0];
          // 2次家屋番号
          const kaokuNumbers = value.slice(1).join(", ");

          // 代表家屋番号をReactコンポーネント化する
          const primaryKaokuNumberComponent = (
            <Typography fontWeight={"bold"}>{primaryKaokuNumber}</Typography>
          );

          // 2次家屋番号をReactコンポーネント化する
          // 列の幅が小さくて隠れていた場合でも気づけるようにツールチップ上でも全項目を表示する
          const kaokuNumbersComponent = (
            <Tooltip title={kaokuNumbers}>
              <Typography>{kaokuNumbers}</Typography>
            </Tooltip>
          );

          return (
            <Box>
              {primaryKaokuNumberComponent}
              {kaokuNumbersComponent}
            </Box>
          );
        }
        return null;
      },
    },
    {
      field: "createdAt",
      type: "dateTime",
      headerName: "作成依頼日時",
      width: 136,
      sortable: false,
      align: "center",
      headerAlign: "center",
      valueFormatter: (params: GridValueFormatterParams<string>) => {
        const value = params.value;
        if (value == null) return "";
        return format(new Date(value), "yyyy/MM/dd HH:mm");
      },
    },
    {
      field: "updatedAt",
      type: "dateTime",
      headerName: "最終更新日時",
      width: 136,
      sortable: false,
      align: "center",
      headerAlign: "center",
      valueFormatter: (params: GridValueFormatterParams<string>) => {
        const value = params.value;
        if (value == null) return "";
        return format(new Date(value), "yyyy/MM/dd HH:mm");
      },
    },
    {
      field: "status",
      headerName: "状態",
      width: 120,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: ({
        id,
        row,
      }: GridRenderCellParams<string, PropertySummaryRow>) => {
        if (row.status === "requesting") {
          return "依頼中";
        } else if (row.status === "succeeded") {
          return "完了";
        } else if (row.status === "failed") {
          return failTooltipMessage(row);
        } else {
          return `不明な状態: ${row.status}`;
        }
      },
    },
    {
      field: "action",
      headerName: "Action",
      minWidth: 90,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: ({
        id,
        row,
      }: GridRenderCellParams<string, PropertySummaryRow>) => (
        <PropertySummaryActionCell
          gridId={id}
          setEditModalOpen={setEditModalOpen}
          setSheetId={setSheetId}
          row={row}
        />
      ),
    },
  ];
};

/**
 * _FeedGrid
 * reload関数を親コンポーネントに外部公開するために、関数名にはアンダースコアをつけている
 * @param _props
 * @param ref
 */
const _FeedGrid: ForwardRefRenderFunction<
  { reload: () => void },
  PropertySummaryFeedGridProps
> = (_props, ref) => {
  let rows: PropertySummaryRow[] = [];
  const { setEditModalOpen, setSheetId } = _props;
  const { apiClient } = useApiClient();
  const { page, pageSize, changePage, changePageSize } = useGridPagination();
  const rowsPerPageOptions: number[] = [25, 50, 100];

  const colDef = useMemo<GridColDef[]>(
    () => makeGridColDef(setEditModalOpen, setSheetId),
    [setEditModalOpen, setSheetId]
  );

  const {
    data: apiResponse,
    isLoading,
    error,
  } = GetPropertySummaryListAPI(apiClient, _props.filterCondition);

  if (
    isAxiosError<GetPropertySummaryListAPIResponse>(error) &&
    (error.response?.status === HttpStatusCode.BadRequest ||
      error.response?.status === HttpStatusCode.NotFound)
  ) {
    toast.error("指定のデータが存在しません。");
  }
  if (
    isAxiosError<GetPropertySummaryListAPIResponse>(error) &&
    (error.response?.status ?? HttpStatusCode.InternalServerError) >=
      HttpStatusCode.InternalServerError
  ) {
    toast.error(
      "データの取得に失敗しました。しばらく経ってから再度お試しください。"
    );
  }

  const transferResponseToRows = (
    apiResponse: GetPropertySummaryListAPIResponse
  ): PropertySummaryRow[] => {
    const apiResponseData: PropertySummaryRow[] = [];

    for (const list of apiResponse.list) {
      apiResponseData.push({
        id: list.id,
        prefectureName: list.prefectureName,
        location: list.location,
        address: list.address,
        chiban: list.chiban,
        kaokuNumbers: [list.primaryKaokuNumber, ...list.kaokuNumbers],
        pictureRequest: list.pictureRequest,
        allMattersAnalyzeRequest: list.allMattersAnalyzeRequest,
        status: list.status,
        updatedAt: list.updatedAt,
        createdAt: list.createdAt,
      });
    }

    return apiResponseData;
  };

  if (isLoading || apiResponse === undefined) {
    return (
      <Stack alignItems="center">
        <CircularProgress sx={{ my: "20px" }} />
      </Stack>
    );
  }

  rows = transferResponseToRows(apiResponse);

  const dataGridPaginationProps = {
    page,
    pageSize,
    rowsPerPageOptions,
    onPageChange: changePage,
    onPageSizeChange: changePageSize,
  };

  return (
    <StyledDataGrid
      stickyHeader={true}
      autoHeight
      disableColumnMenu={true}
      disableSelectionOnClick={true}
      rows={rows}
      columns={colDef}
      {...dataGridPaginationProps}
    />
  );
};

/**
 * FeedGrid
 * forwardRefを使ってreload()を外部から呼び出せるようにしている
 */
export const PropertySummaryFeedGrid = forwardRef<
  { reload: () => void },
  PropertySummaryFeedGridProps
>(_FeedGrid);
